import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { FieldSelect } from "@/components/ui/field-select";
import { fmtDateTime } from "./adminUtils";
import { Loader2, ScrollText, ArrowRight } from "lucide-react";

const ACTION_LABEL = {
  "org.suspend": "Organisation suspendue", "org.reactivate": "Organisation réactivée",
  "org.plan_change": "Changement de plan", "support.status_change": "Statut ticket modifié",
  "support.priority_change": "Priorité ticket modifiée",
};
const TARGET_LABEL = { organization: "Organisation", support_ticket: "Ticket support" };

function readable(obj) {
  if (!obj || typeof obj !== "object") return "—";
  return Object.entries(obj).map(([k, v]) => `${k}: ${v ?? "—"}`).join(", ");
}

export default function AdminAuditLogs() {
  const [action, setAction] = useState("all"); const [dateFrom, setDateFrom] = useState(""); const [page, setPage] = useState(1);
  const pageSize = 30;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit-logs", action, dateFrom, page],
    queryFn: async () => (await api.get("/api/admin/audit-logs", { params: {
      action: action === "all" ? undefined : action, date_from: dateFrom || undefined, page, page_size: pageSize } })).data,
    retry: false, keepPreviousData: true,
  });
  const items = data?.items || []; const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const actionOpts = [{ value: "all", label: "Toutes les actions" }, ...(data?.actions || []).map((a) => ({ value: a, label: ACTION_LABEL[a] || a }))];

  return (
    <div data-testid="admin-audit-logs">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Logs admin</h1>
      <p className="mt-1 text-sm text-slate-500">Traçabilité des actions Super Admin (avant / après)</p>

      <div className="mb-4 mt-5 flex flex-wrap items-center gap-2">
        <FieldSelect testId="admin-logs-filter-action" className="w-56" value={action} onChange={(v) => { setAction(v); setPage(1); }} options={actionOpts} />
        <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} data-testid="admin-logs-filter-date"
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 focus:border-primary focus:outline-none" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="admin-logs-empty">
          <ScrollText className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucun log ne correspond à ces filtres.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-logs-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>{["Date", "Super Admin", "Action", "Organisation", "Cible", "Changement"].map((h) => <th key={h} className="whitespace-nowrap px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((i) => (
                <tr key={i.id} data-testid={`admin-log-row-${i.id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateTime(i.created_at)}</td>
                  <td className="px-4 py-3 text-slate-600">{i.super_admin_email || "—"}</td>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">{ACTION_LABEL[i.action] || i.action}</span></td>
                  <td className="px-4 py-3 text-slate-700">{i.organization_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{TARGET_LABEL[i.target_type] || i.target_type}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="rounded bg-rose-50 px-2 py-0.5 text-rose-600">{readable(i.before)}</span>
                      <ArrowRight className="h-3 w-3 shrink-0 text-slate-300" />
                      <span className="rounded bg-emerald-50 px-2 py-0.5 text-emerald-700">{readable(i.after)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>{total} entrée{total > 1 ? "s" : ""}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-logs-prev" className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Précédent</button>
            <span>Page {page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-logs-next" className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Suivant</button>
          </div>
        </div>
      )}
    </div>
  );
}
